import React, { useEffect, useRef, useState } from 'react';
import { Field, Form, Table } from './CkOperationsShared';

const STEP = { consumed_by_batch: 'Used in batch', produced: 'Batch output', transfer: 'Transferred', sold: 'Sold' };
const REASON = {
  max_nodes: 'node limit reached', max_depth: 'depth limit reached',
  max_batches_per_lot: 'too many batches for one lot', max_consumptions_per_lot: 'too many sales for one lot',
  max_consumptions_per_order: 'too many lot lines on one order',
};

/** Edges in journey order: topological over the nodes (upstream first), stable otherwise. */
export function orderedSteps(trace) {
  const edges = trace?.edges || [];
  const incoming = new Map((trace?.nodes || []).map(n => [n.key, 0]));
  edges.forEach(e => incoming.set(e.to, (incoming.get(e.to) || 0) + 1));
  const queue = [...incoming].filter(([, n]) => n === 0).map(([k]) => k);
  const rank = new Map();
  while (queue.length) {
    const key = queue.shift();
    rank.set(key, rank.size);
    edges.filter(e => e.from === key).forEach(e => { incoming.set(e.to, incoming.get(e.to) - 1); if (incoming.get(e.to) === 0) queue.push(e.to); });
  }
  return edges.map((e, i) => ({ e, i })).sort((a, b) => ((rank.get(a.e.from) ?? 1e9) - (rank.get(b.e.from) ?? 1e9)) || a.i - b.i).map(x => x.e);
}

function siteName(trace, id) { return (trace.sites && trace.sites[id]) || id || 'Unknown site'; }

export function describe(trace, node) {
  if (!node) return 'Unknown';
  const at = `at ${siteName(trace, node.site_id)}`;
  if (node.type === 'lot') return `Lot ${node.id} (${node.item_name || node.item_id || 'unknown item'}) ${at}`;
  if (node.type === 'batch') return `Batch ${node.id} (${node.item_name || node.recipe_id || 'unknown recipe'}) ${at}`;
  return `Order ${node.order_number || node.id} ${at}, table ${node.table_number || node.table_id || 'not recorded'}`;
}

function detail(trace, e) {
  const qty = e.qty ? `${e.qty} ${e.uom || ''}`.trim() : '';
  if (e.kind === 'transfer') return [qty, e.transfer_id && `transfer ${e.transfer_id}`, `${siteName(trace, e.from_site_id)} → ${siteName(trace, e.to_site_id)}`, e.at && `received ${e.at}`].filter(Boolean).join(' · ');
  if (e.kind === 'sold') return [qty, e.plu && `PLU ${e.plu}`, e.sold_at && `sold ${e.sold_at}`, `table ${e.table_number || e.table_id || 'not recorded'}`, e.customer_id && `customer ${e.customer_id}`].filter(Boolean).join(' · ');
  return qty;
}

export default function TraceExplorer({ trace }) {
  const nodes = new Map((trace.nodes || []).map(n => [n.key, n]));
  const root = nodes.get(trace.root?.key);
  const steps = orderedSteps(trace);
  const showCost = (trace.supplier_lots || []).some(s => 'unit_cost' in s);
  return <section className="ck-detail" aria-label="Lot journey">
    <h2>Journey: {describe(trace, root)}</h2>
    <p>{trace.caption}</p>
    {trace.truncated && <p role="alert">Incomplete trace: {(trace.truncation_reasons || []).map(r => REASON[r] || r).join(', ')}. Limits: {trace.limits?.max_nodes} records, depth {trace.limits?.max_depth}.</p>}
    {!trace.cross_site && <p>Showing {siteName(trace, trace.site_id)} only. Owners and admins can follow the journey across sites.</p>}
    <h3>Steps</h3>
    {steps.length ? <ol className="ck-trace-steps">{steps.map((e, i) => <li key={i}><strong>{STEP[e.kind] || e.kind}:</strong> {describe(trace, nodes.get(e.from))} → {describe(trace, nodes.get(e.to))}{detail(trace, e) && <span> ({detail(trace, e)})</span>}</li>)}</ol>
      : <p>No links recorded for this {trace.root?.type || 'record'}. This does not prove there are none.</p>}
    <h3>Supplier lots</h3>
    <Table headers={['Lot', 'Item', 'Site', 'Supplier', 'Supplier lot', 'Received', ...(showCost ? ['Unit cost'] : [])]} empty={!trace.supplier_lots?.length}>{(trace.supplier_lots || []).map(s => <tr key={s.id}><td>{s.id}</td><td>{s.item_name || s.item_id}</td><td>{siteName(trace, s.site_id)}</td><td>{s.supplier_id || 'Not recorded'}</td><td>{s.supplier_lot_ref || 'Not recorded'}</td><td>{s.received_at || 'Unknown'}</td>{showCost && <td className="ck-number">{s.unit_cost == null ? 'Unknown' : `${s.unit_cost} / ${s.uom || 'unit'}`}</td>}</tr>)}</Table>
    <h3>Orders</h3>
    <Table headers={['Order', 'Site', 'Table', 'Closed', 'Customer']} empty={!trace.orders?.length}>{(trace.orders || []).map(o => <tr key={o.id}><td>{o.order_number || o.id}</td><td>{siteName(trace, o.site_id)}</td><td>{o.table_number || o.table_id || 'Not recorded'}</td><td>{o.closed_at || 'Not closed'}</td><td>{o.customer_id || 'Walk-in'}</td></tr>)}</Table>
  </section>;
}

export function TraceLookup({ onTrace, busy, initial }) {
  const [kind, setKind] = useState(initial?.kind || 'order');
  const [value, setValue] = useState(initial?.id || '');
  const ran = useRef(false);
  useEffect(() => { if (initial?.id && !ran.current) { ran.current = true; onTrace(initial.kind, initial.id); } }, [initial, onTrace]);
  return <Form title="Trace a lot or order" submit="Trace" busy={busy} onSubmit={() => value.trim() && onTrace(kind, value.trim())}>
    <Field label="Trace from" value={kind} onChange={e => setKind(e.target.value)} options={[{ value: 'order', label: 'POS order' }, { value: 'lot', label: 'Lot' }]} />
    <Field label="Order or lot ID" value={value} onChange={e => setValue(e.target.value)} />
  </Form>;
}

export function tracePath(kind, id) {
  const enc = encodeURIComponent(id);
  return kind === 'lot' ? `/api/trace/lot/${enc}?direction=both` : `/api/trace/order/${enc}`;
}
